// views/Profile/components/QuotasTable.jsx
import React from 'react';
import { Gauge, AlertCircle, Check, Slash } from 'lucide-react';
import EmptyState from '../../../components/ui/EmptyState';
import './QuotasTable.css';

const formatNumber = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const RESOURCE_LABELS = {
  WORKFLOW_EXECUTION: 'Workflow execution',
  ASSISTANT_REQUEST: 'Agent request',
  ASSISTANT_CALL: 'Agent call',
  DATABASE_CALL: 'Database call',
  DATABASE_QUERY: 'Database query',
  DATA_TRANSFER_GB: 'Data transfer',
  TABLE_OPERATION: 'Table operation',
};

const humanizeResource = (raw) => {
  if (!raw) return '—';
  if (RESOURCE_LABELS[raw]) return RESOURCE_LABELS[raw];
  return raw
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Convert window hours to human-friendly labels. Backend canonical
 * set is {1, 6, 12, 24, 168, 720}; we cover those plus a generic
 * fallback for unexpected values.
 */
const humanizeWindow = (hours) => {
  if (hours == null) return '—';
  switch (hours) {
    case 1:
      return '1 hour';
    case 6:
      return '6 hours';
    case 12:
      return '12 hours';
    case 24:
      return '24 hours';
    case 168:
      return '7 days';
    case 720:
      return '30 days';
    default:
      if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
      if (hours % 24 === 0) {
        const d = hours / 24;
        return `${d} day${d === 1 ? '' : 's'}`;
      }
      return `${hours} hours`;
  }
};

const METRIC_LABEL = {
  COUNT: 'Count',
  COINS: 'Coins',
};

/**
 * QuotasTable — read-only listing of org-level QuotaPolicy rows.
 *
 * Backend has no public upsert today, so the UI explicitly states
 * that the table is read-only and points to support for changes. The
 * status pill is green for enabled, dim for disabled. windowHours
 * gets humanized to "1 hour" / "24 hours" / "7 days" / "30 days" so
 * admins don't have to do mental math.
 */
export default function QuotasTable({
  data = [],
  loading = false,
  error = null,
}) {
  const showSkeleton = loading && (!data || data.length === 0);
  const showEmpty = !loading && !error && (!data || data.length === 0);

  return (
    <section className="quotas-table">
      <header className="quotas-table__header">
        <div className="quotas-table__title">
          <Gauge size={16} aria-hidden="true" />
          <h2>Quotas</h2>
        </div>
        <span className="quotas-table__readonly-tag">Read-only</span>
      </header>

      <p className="quotas-table__hint">
        Per-resource caps applied across the organization. Contact
        support to add or modify a quota.
      </p>

      {showSkeleton && (
        <div className="quotas-table__skeleton" aria-busy="true">
          <div className="quotas-table__skel-row" />
          <div className="quotas-table__skel-row" />
          <div className="quotas-table__skel-row" />
        </div>
      )}

      {error && (
        <div className="quotas-table__error">
          <AlertCircle size={20} aria-hidden="true" />
          <p>{error.message || 'Unable to load quotas'}</p>
          {error.requestId && (
            <small className="quotas-table__request-id">
              ref: {error.requestId}
            </small>
          )}
        </div>
      )}

      {showEmpty && (
        <EmptyState
          variant="neutral"
          description="No quota policies configured for this organization."
        />
      )}

      {!showSkeleton && !error && data && data.length > 0 && (
        <div className="quotas-table__scroll">
          <table className="quotas-table__grid">
            <thead>
              <tr>
                <th scope="col">Resource</th>
                <th scope="col">Metric</th>
                <th scope="col">Window</th>
                <th scope="col" className="quotas-table__th-right">
                  Max
                </th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const enabled = Boolean(row.isEnabled);
                return (
                  <tr key={row.id} className="quotas-table__row">
                    <td className="quotas-table__resource">
                      {humanizeResource(row.resourceType)}
                      {row.description && (
                        <small className="quotas-table__resource-desc">
                          {row.description}
                        </small>
                      )}
                    </td>
                    <td className="quotas-table__metric">
                      {METRIC_LABEL[row.metric] ?? row.metric}
                    </td>
                    <td className="quotas-table__window">
                      {humanizeWindow(row.windowHours)}
                    </td>
                    <td className="quotas-table__max">
                      {formatNumber(row.maxCount)}
                    </td>
                    <td>
                      <span
                        className={`quotas-table__status quotas-table__status--${
                          enabled ? 'enabled' : 'disabled'
                        }`}
                      >
                        {enabled ? (
                          <Check size={12} aria-hidden="true" />
                        ) : (
                          <Slash size={12} aria-hidden="true" />
                        )}
                        {enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
